import React from "react";
import useExcelUpload from "../../../../utils/hooks/useExcelUpload";
import UploadExcelDialog from "../../utils/UploadExcelEnhanced";

const fixedSheetNames = ["S19A", "S19", "S19B", "S19D", "S16"];
const fixedColumns = {
  S19A: ["ya", "caName", "noOfYearClaimsBf", "twdvBf"],
  S19: ["ya", "caName", "noOfYearClaims", "noOfYearClaimsBf", "twdvBf"],
  S19B: [
    "ya",
    "caName",
    "noOfYearClaims",
    "noOfYearClaimsBf",
    "baseTwdvBf",
    "enhancedTwdvBf",
  ],
  S19D: ["ya", "caName", "noOfYearClaims", "noOfYearClaimsBf", "twdvBf"],
  S16: [
    "Year of Assessment",
    "caName",
    "caSummary",
    "noOfYearClaims",
    "noOfYearClaimsBf",
    "twdvBf",
    "isDisregard",
    "disregardRate",
  ],
};

const instructions = [
  ["Instructions"],
  [
    "Step 1:",
    "Create the requisite CA Schedules from the side panel -> CA Schedules",
  ],
  [
    "Step 2:",
    "Populate the respective CA schedules (worksheets in this workbook) with prior year data",
  ],
  ["Step 3:", "Upload prior YA CA claims in any of the CA Schedules"],
  [],
  ["Common errors", "Error message", "Comments", "Solution"],
  [
    "1.",
    "Please create CA schedule before uploading prior year CA claims.",
    "Failure to create respective CA schedule(s) before uploading the prior year CA claims.",
    "1. Create the requisite CA Schedules from the side panel -> CA Schedules\n2. Check that schedules that are not supposed to be uploaded have no data",
  ],
  [
    "2.",
    "CA name mismatch.",
    "CA Name does not match system requirements.",
    "Ensure that CA name used in the upload template is from the list of CA Name below",
  ],
  [],
  ["Col", "Comments"],
  ["S19A"],
  [
    "Year of assessment",
    "Year of assessment where capital allowance was incurred in YYYY format (e.g. 2024)",
  ],
  [
    "CA Name",
    "Ensure that data is based on the following options: S19A(1) 3 years, S19A(1) 2 years, S19A(2 to 10) 1 year, S19A(10A) 1 year - capped",
  ],
  ["No. of years b/f", "No. of years brought forward from previous YA"],
  ["TWDV b/f", "Tax written down value brought forward from previous YA"],
  [],
  ["S19"],
  [
    "Year of assessment",
    "Year of assessment where capital allowance was incurred in YYYY format (e.g. 2024)",
  ],
  ["CA Name", "Ensure that data is based on the following options: S19"],
  ["No. of years of claim", "No. of years of claim"],
  ["No. of years b/f", "No. of years brought forward from previous YA"],
  ["TWDV b/f", "Tax written down value brought forward from previous YA"],
  [],
  ["S19B"],
  [
    "Year of assessment",
    "Year of assessment where capital allowance was incurred in YYYY format (e.g. 2024)",
  ],
  [
    "CA Name",
    "Ensure that data is based on the following options: S19B YA 2024 - 28, S19B",
  ],
  ["No. of years of claim", "No. of years of claim"],
  ["No. of years b/f", "No. of years brought forward from previous YA"],
  [
    "Base TWDV b/f",
    "Base amount fo tax written down value brought forward from previous YA",
  ],
  [
    "Enhanced TWDV b/f",
    "[Optional] Enhanced amount fo tax written down value brought forward from previous YA",
  ],
  [],
  ["S19D"],
  [
    "Year of assessment",
    "Year of assessment where capital allowance was incurred in YYYY format (e.g. 2024)",
  ],
  ["CA Name", "Ensure that data is based on the following options: S19D"],
  ["No. of years of claim", "No. of years of claim"],
  ["No. of years b/f", "No. of years brought forward from previous YA"],
  ["TWDV b/f", "Tax written down value brought forward from previous YA"],
  [],
  ["S16"],
  [
    "Year of assessment",
    "Year of assessment where capital allowance was incurred in YYYY format (e.g. 2024)",
  ],
  [
    "CA Name",
    "Ensure that data is based on the following options: S16 - (LIA / IBA)",
  ],
  [
    "CA Summary",
    "Ensure that data is based on the following options: S16 - IBA, S16 - LIA",
  ],
  ["No. of years of claim", "No. of years of claim"],
  ["No. of years b/f", "No. of years brought forward from previous YA"],
  ["TWDV b/f", "Tax written down value brought forward from previous YA"],
  [
    "Disregard",
    "TRUE if a certain percentage of CA claim is to be disregarded for non-qualifying use; FALSE if there is no need to disregard S16 claims",
  ],
  [
    "Disregard rate (%)",
    "[Optional] Indicate disregard percentage as a number (without % sign), up to 4 decimal place",
  ],
];

const CaExcelUpload = ({
  open,
  handleCloseDialog,
  handleExcelUpload,
  processing = false,
}) => {
  const {
    selectedFile,
    uploading,
    alertMessage,
    alertSeverity,
    errorFileUrl,
    rowCount,
    handleCloseAlert,
    handleDownloadMultipleTemplate,
    handleFileMultiSheetChange,
    handleResetFile,
    handleUploadMultiSheet,
  } = useExcelUpload({
    fileName: "prior_year_ca_upload_template.xlsx",
    sheets: [
      {
        name: "Instructions",
        data: instructions,
        columnWidths: [18],
        type: "aoa",
      },
      {
        name: "S19A",
        data: [
          ["Year of Assessment", "CA Name", "No. of years b/f", "TWDV b/f"],
        ],
        type: "aoa",
      },
      {
        name: "S19",
        data: [
          [
            "Year of Assessment",
            "CA Name",
            "No. of years of claim",
            "No. of years b/f",
            "TWDV b/f",
          ],
        ],
        type: "aoa",
      },
      {
        name: "S19B",
        data: [
          [
            "Year of Assessment",
            "CA Name",
            "No. of years of claim",
            "No. of years b/f",
            "Base TWDV b/f",
            "Enhanced TWDV b/f",
          ],
        ],
        type: "aoa",
      },
      {
        name: "S19D",
        data: [
          [
            "Year of Assessment",
            "CA Name",
            "No. of years of claim",
            "No. of years b/f",
            "TWDV b/f",
          ],
        ],
        type: "aoa",
      },
      {
        name: "S16",
        data: [
          [
            "Year of Assessment",
            "CA Name",
            "CA Summary",
            "No. of years of claim",
            "No. of years b/f",
            "TWDV b/f",
            "Disregard",
            "Disregard rate (%)",
          ],
        ],
        type: "aoa",
      },
    ],
    validationSchema: [],
    onUpload: (excelData, rowCount) => {
      let s19rows = [];
      let s19arows = [];
      let s19brows = [];
      let s19drows = [];
      let s16rows = [];

      fixedSheetNames.forEach((sheetName) => {
        if (excelData[sheetName] && excelData[sheetName].length > 0) {
          const data = excelData[sheetName];

          if (data.length > 0) {
            const columns = fixedColumns[sheetName];
            const mappedData = data.map((row) =>
              Object.fromEntries(columns.map((key, index) => [key, row[index]]))
            );
            if (sheetName === "S19") {
              s19rows = mappedData;
            } else if (sheetName === "S19A") {
              s19arows = mappedData;
            } else if (sheetName === "S19B") {
              s19brows = mappedData;
            } else if (sheetName === "S19D") {
              s19drows = mappedData;
            } else if (sheetName === "S16") {
              s16rows = mappedData;
            }
          }
        }
      });

      handleExcelUpload({
        S19: s19rows,
        S19A: s19arows,
        S19B: s19brows,
        S19D: s19drows,
        S16: s16rows,
      });
    },
  });

  return (
    <UploadExcelDialog
      open={open}
      selectedFile={selectedFile}
      uploading={uploading}
      processing={processing}
      alertMessage={alertMessage}
      alertSeverity={alertSeverity}
      errorFileUrl={errorFileUrl}
      validRows={rowCount}
      onClose={handleCloseDialog}
      onDownloadTemplate={handleDownloadMultipleTemplate}
      onFileChange={handleFileMultiSheetChange}
      onUpload={handleUploadMultiSheet}
      onCloseAlert={handleCloseAlert}
      onReset={handleResetFile}
    />
  );
};

export default CaExcelUpload;
